import { useId } from "react";

type Palette = { sky: [string, string, string, string, string]; sun: string; glow: string; far: string; castle: string; castleLit: string; hillA: [string, string]; hillB: [string, string]; tree: string };
export type ScenePalette = "dusk" | "day" | "rose" | "night" | "mint" | "ocean";
/** what stands on the horizon: the fairy-tale castles of the front page, a lit city (ranking), snowy peaks (goals), a market (shop), a school (class) */
export type SceneKind = "castles" | "city" | "peaks" | "market" | "school";

const PALETTES: Record<ScenePalette, Palette> = {
  dusk: { sky: ["#2a1470", "#6a34d8", "#ff6fb1", "#ffb04d", "#ffe08a"], sun: "#fff3b0", glow: "#ffd070", far: "#6b3fbf", castle: "#3b1f86", castleLit: "#ffd45c", hillA: ["#2ee6a6", "#0b9d83"], hillB: ["#1ecf7e", "#0a8f58"], tree: "#0a6d45" },
  day: { sky: ["#3f8cff", "#78b8ff", "#bfe3ff", "#e8f6ff", "#fff3c9"], sun: "#fffbe0", glow: "#fff0b0", far: "#8fa8f0", castle: "#5b6fd0", castleLit: "#ffe27a", hillA: ["#6fe39a", "#25b36c"], hillB: ["#3fd37a", "#169c58"], tree: "#0f7a47" },
  rose: { sky: ["#6a1b9a", "#c2358f", "#ff7a6b", "#ffb86b", "#ffe9b0"], sun: "#fff6d0", glow: "#ffb070", far: "#a23a9e", castle: "#6c1f7f", castleLit: "#ffe27a", hillA: ["#5fe3a1", "#1aa878"], hillB: ["#38d08a", "#0e8f62"], tree: "#0b6b4a" },
  night: { sky: ["#070c33", "#16237c", "#4636b8", "#8a45cf", "#e07cd0"], sun: "#fff7d6", glow: "#bfa8ff", far: "#33399a", castle: "#1a2062", castleLit: "#ffd45c", hillA: ["#2a9d94", "#0f6470"], hillB: ["#1d8a80", "#0a505e"], tree: "#0a4a4c" },
  mint: { sky: ["#2a86d0", "#4fc3dc", "#9aeed8", "#e0fff0", "#fff7c4"], sun: "#fffbe0", glow: "#fff2a8", far: "#58a8c8", castle: "#3a7fb4", castleLit: "#ffe27a", hillA: ["#8be86f", "#3bb34a"], hillB: ["#5fdc6a", "#259a43"], tree: "#157a3a" },
  ocean: { sky: ["#1b64d8", "#3f9cf2", "#8ad0ff", "#d6f0ff", "#fff3cd"], sun: "#fffbe0", glow: "#ffe9a0", far: "#6a98e8", castle: "#4a6ad0", castleLit: "#ffe27a", hillA: ["#58dcb0", "#1fa985"], hillB: ["#34c993", "#10936f"], tree: "#0d7a58" },
};

function Castle({ x, y, s = 1, flip = false, p }: { x: number; y: number; s?: number; flip?: boolean; p: Palette }) {
  return (
    <g transform={`translate(${x} ${y}) scale(${flip ? -s : s} ${s})`}>
      <rect x="0" y="70" width="150" height="80" fill={p.castle} />
      <rect x="-10" y="20" width="34" height="130" fill={p.castle} />
      <rect x="58" y="0" width="42" height="150" fill={p.castle} />
      <rect x="120" y="32" width="34" height="118" fill={p.castle} />
      <polygon points="-14,20 7,-24 28,20" fill={p.castle} />
      <polygon points="54,0 79,-52 104,0" fill={p.castle} />
      <polygon points="116,32 137,-4 158,32" fill={p.castle} />
      <path d="M79 -52 V-84 L104 -76 L79 -68" fill="#ff5c8a" stroke={p.castle} strokeWidth="2" />
      {[[2, 40], [2, 70], [70, 24], [70, 58], [70, 92], [130, 56], [130, 86], [34, 98], [100, 100]].map(([wx, wy], i) => (
        <rect key={i} x={wx! + 4} y={wy} width="8" height="14" rx="4" fill={p.castleLit} opacity={0.9} />
      ))}
      <path d="M52 150 v-30 a12 12 0 0 1 24 0 v30z" fill="#1d0f52" opacity="0.55" />
    </g>
  );
}

/** a skyline with lit windows – the "arena" of the ranking page */
function City({ p }: { p: Palette }) {
  const blocks: [number, number, number][] = [[20, 70, 150], [98, 54, 210], [160, 80, 120], [250, 60, 240], [318, 90, 170], [420, 50, 130], [900, 64, 190], [972, 56, 250], [1036, 84, 150], [1128, 60, 210], [1190, 40, 130]];
  return (
    <g>
      {blocks.map(([x, w, h], i) => (
        <g key={i}>
          <rect x={x} y={420 - h} width={w} height={h + 4} fill={p.castle} />
          {Array.from({ length: Math.floor(h / 26) }, (_, r) => Array.from({ length: Math.max(1, Math.floor(w / 20)) }, (_, c) => (
            <rect key={`${r}-${c}`} x={x + 8 + c * 18} y={420 - h + 10 + r * 24} width="8" height="12" rx="2" fill={p.castleLit} opacity={(r * 7 + c * 3 + i) % 5 === 0 ? 0.15 : 0.85} />
          )))}
        </g>
      ))}
      {[250, 972].map((x, i) => <polygon key={i} points={`${x + 6},${420 - (i ? 250 : 240)} ${x + 30},${420 - (i ? 250 : 240) - 58} ${x + 54},${420 - (i ? 250 : 240)}`} fill={p.castle} />)}
    </g>
  );
}

/** three big snowy peaks, the highest with a flag – the "goals" page */
function Peaks({ p }: { p: Palette }) {
  return (
    <g>
      <polygon points="40,430 250,170 460,430" fill={p.castle} opacity="0.9" />
      <polygon points="250,170 300,238 270,228 250,252 226,226 202,240" fill="#fff" opacity="0.95" />
      <polygon points="330,430 600,90 870,430" fill={p.castle} />
      <polygon points="600,90 668,184 630,170 600,208 566,168 530,186" fill="#fff" />
      <path d="M600 90 V40" stroke="#fff" strokeWidth="4" strokeLinecap="round" />
      <path d="M602 42 L650 56 L602 70Z" fill="#ff5c8a" />
      <polygon points="780,430 980,200 1180,430" fill={p.castle} opacity="0.92" />
      <polygon points="980,200 1026,262 1000,254 980,276 958,252 934,262" fill="#fff" opacity="0.95" />
    </g>
  );
}

/** four striped market stalls with bunting – the "shop" page */
function Market({ p }: { p: Palette }) {
  const stalls: [number, string, string][] = [[90, "#ff5c8a", "#fff"], [350, "#ffc61a", "#fff"], [800, "#38c8f5", "#fff"], [1040, "#7dff8a", "#fff"]];
  return (
    <g>
      <path d="M0 150 Q300 230 600 160 T1200 150" fill="none" stroke="#fff" strokeOpacity="0.8" strokeWidth="3" />
      {Array.from({ length: 22 }, (_, i) => {
        const x = 30 + i * 54;
        const y = 150 + Math.sin((x / 1200) * Math.PI * 2 * 1.5) * 38 + (x < 600 ? 30 : 8);
        return <polygon key={i} points={`${x - 14},${y} ${x + 14},${y} ${x},${y + 30}`} fill={["#ff5c8a", "#ffc61a", "#38c8f5", "#7dff8a", "#b18cff"][i % 5]} />;
      })}
      {stalls.map(([x, c1, c2], i) => (
        <g key={i} transform={`translate(${x} 316)`}>
          <rect x="6" y="40" width="148" height="80" fill={p.castle} />
          <rect x="22" y="58" width="116" height="40" rx="6" fill={p.castleLit} opacity="0.85" />
          <path d="M0 40 L20 0 H140 L160 40Z" fill={c1} />
          {[0, 1, 2, 3].map((k) => <path key={k} d={`M${20 + k * 30} 0 H${35 + k * 30} L${30 + k * 30 + (k < 2 ? -6 : 6) * 0} 40 H${15 + k * 32}Z`} fill={c2} opacity="0.92" transform={`translate(${k * 2} 0)`} />)}
          <path d="M-4 40 q10 18 21 0 q10 18 21 0 q10 18 21 0 q10 18 21 0 q10 18 21 0 q10 18 21 0 q10 18 21 0 q10 18 21 0" fill="none" stroke={c1} strokeWidth="9" strokeLinecap="round" />
        </g>
      ))}
    </g>
  );
}

/** a school building with a clock and a flag – the "class" page */
function School({ p }: { p: Palette }) {
  return (
    <g transform="translate(150 190)">
      <rect x="0" y="90" width="300" height="140" fill={p.castle} />
      <rect x="-20" y="70" width="340" height="30" fill={p.castle} opacity="0.85" />
      <polygon points="110,90 150,10 190,90" fill={p.castle} />
      <circle cx="150" cy="62" r="20" fill="#fff" /><path d="M150 62 V48 M150 62 L160 68" stroke="#14246a" strokeWidth="3" strokeLinecap="round" />
      <path d="M150 10 V-40" stroke="#fff" strokeWidth="4" strokeLinecap="round" /><path d="M152 -38 L196 -26 L152 -14Z" fill="#ff5c8a" />
      {[20, 70, 200, 250].map((x) => <rect key={x} x={x} y="120" width="28" height="34" rx="4" fill={p.castleLit} opacity="0.9" />)}
      {[20, 70, 200, 250].map((x) => <rect key={"b" + x} x={x} y="170" width="28" height="34" rx="4" fill={p.castleLit} opacity="0.55" />)}
      <path d="M120 230 v-44 a30 30 0 0 1 60 0 v44z" fill="#1d0f52" opacity="0.55" />
    </g>
  );
}

/**
 * A crisp, drawn landscape that scales to any width. Decorative only.
 * The layers carry `par1…par4` classes: a script-free CSS parallax moves them a little with the pointer (`--px`, `--py` on the scene's
 * parent, set by the global pointer helper) – far things move little, near things more.
 */
export function HeroScene({ palette = "dusk", scene = "castles" }: { palette?: ScenePalette; scene?: SceneKind }) {
  const id = useId().replace(/[^a-zA-Z0-9]/g, "");
  const p = PALETTES[palette];
  return (
    <svg className="scene" viewBox="0 0 1200 480" preserveAspectRatio="xMidYMax slice" aria-hidden="true" focusable="false">
      <defs>
        <linearGradient id={`sky${id}`} x1="0" y1="0" x2="0" y2="1">
          {p.sky.map((c, i) => <stop key={i} offset={[0, 0.34, 0.62, 0.82, 1][i]} stopColor={c} />)}
        </linearGradient>
        <radialGradient id={`sun${id}`} cx="0.5" cy="0.5" r="0.5">
          <stop offset="0" stopColor="#fff6c0" stopOpacity="0.95" />
          <stop offset="0.35" stopColor={p.glow} stopOpacity="0.55" />
          <stop offset="1" stopColor="#ff9f4a" stopOpacity="0" />
        </radialGradient>
        <linearGradient id={`ha${id}`} x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor={p.hillA[0]} /><stop offset="1" stopColor={p.hillA[1]} /></linearGradient>
        <linearGradient id={`hb${id}`} x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor={p.hillB[0]} /><stop offset="1" stopColor={p.hillB[1]} /></linearGradient>
      </defs>
      <rect width="1200" height="480" fill={`url(#sky${id})`} />
      {palette === "night" && (
        <g fill="#fff">
          {Array.from({ length: 46 }, (_, i) => <circle key={i} cx={(i * 131 + 40) % 1200} cy={(i * 53 + 14) % 250} r={0.8 + ((i * 7) % 3) * 0.6} opacity={0.35 + ((i * 11) % 6) / 10} />)}
        </g>
      )}
      <circle cx="600" cy="352" r="300" fill={`url(#sun${id})`} />
      <circle cx="600" cy="352" r="64" fill={p.sun} opacity="0.95" />
      <g className="clouds" fill="#fff" opacity={palette === "night" ? 0.14 : 0.38}>
        <g className="cl a"><ellipse cx="170" cy="130" rx="92" ry="20" /><ellipse cx="230" cy="116" rx="52" ry="18" /></g>
        <g className="cl b"><ellipse cx="930" cy="96" rx="110" ry="22" /><ellipse cx="880" cy="84" rx="56" ry="18" /></g>
        <g className="cl c"><ellipse cx="560" cy="176" rx="80" ry="15" /><ellipse cx="610" cy="166" rx="42" ry="13" /></g>
      </g>
      <g className="par1"><path d="M0 372 L96 312 L190 352 L300 270 L420 356 L520 318 L600 350 L690 316 L800 276 L920 352 L1030 296 L1120 340 L1200 308 V480 H0Z" fill={p.far} opacity="0.72" /></g>
      <g className="par2">
        {scene === "castles" && <><Castle x={140} y={228} s={1.05} p={p} /><Castle x={1056} y={246} s={0.9} flip p={p} /></>}
        {scene === "city" && <City p={p} />}
        {scene === "peaks" && <Peaks p={p} />}
        {scene === "market" && <Market p={p} />}
        {scene === "school" && <><School p={p} /><Castle x={1056} y={262} s={0.78} flip p={p} /></>}
      </g>
      <g className="par3">
        <path d="M0 404 C140 360 260 372 400 398 C520 420 640 392 760 384 C900 374 1040 396 1200 372 V480 H0Z" fill={`url(#ha${id})`} />
        <path d="M0 440 C160 412 300 424 450 438 C600 452 760 424 900 424 C1040 424 1120 440 1200 432 V480 H0Z" fill={`url(#hb${id})`} />
      </g>
      <g className="par4">
        {[[70, 424, 1], [236, 434, 0.8], [980, 420, 1.05], [1120, 426, 0.85], [356, 446, 0.7], [820, 446, 0.75]].map(([tx, ty, ts], i) => (
          <g key={i} transform={`translate(${tx} ${ty}) scale(${ts})`}>
            <rect x="-3" y="-4" width="6" height="26" rx="2" fill="#5a3a12" />
            <circle cx="0" cy="-22" r="19" fill={p.tree} />
            <circle cx="-9" cy="-14" r="11" fill={p.tree} />
            <circle cx="10" cy="-14" r="11" fill={p.tree} />
          </g>
        ))}
      </g>
    </svg>
  );
}
